import { cva } from "class-variance-authority";
import { cn } from "cn";
import { AlertCircle } from "lucide-react";
import type { ComponentProps, FC, HTMLAttributes } from "react";
import React from "react";

import {
  TableCell as BaseTableCell,
  TableHead as BaseTableHead,
  TableRow as BaseTableRow,
} from "../shadcn/ui/table.tsx";

import type { VariantProps } from "./lib.ts";

export * from "../shadcn/ui/table.tsx";

export const TableCard: FC<HTMLAttributes<HTMLDivElement>> = ({
  className,
  ...props
}) => (
  <div
    className={cn("overflow-hidden rounded-xl border bg-card", className)}
    {...props}
  />
);

const tableCellVariants = cva("", {
  defaultVariants: { variant: "default" },
  variants: {
    variant: {
      default: "font-medium text-foreground",
      error: "py-8 text-center text-destructive",
      info: "py-8 text-center text-muted-foreground",
    },
  },
});

interface TableCellProps
  extends ComponentProps<typeof BaseTableCell>,
    VariantProps<typeof tableCellVariants> {}

const TableCellContent: FC<Pick<TableCellProps, "children" | "variant">> = ({
  children,
  variant,
}) => {
  switch (variant) {
    case "default":
    case "info":
    case null:
    case undefined:
      return <>{children}</>;
    case "error":
      return (
        <div className="flex items-center justify-center gap-2">
          <AlertCircle className="size-4 shrink-0" />
          <span>{children}</span>
        </div>
      );
  }
};

export const TableCell: FC<TableCellProps> = ({
  children,
  className,
  variant,
  ...props
}) => (
  <BaseTableCell
    className={cn(
      "h-11 px-3 py-1 text-sm",
      tableCellVariants({ variant }),
      className,
    )}
    {...props}
  >
    <TableCellContent variant={variant}>{children}</TableCellContent>
  </BaseTableCell>
);

export const TableHead: typeof BaseTableHead = ({ className, ...props }) => (
  <BaseTableHead
    className={cn(
      "px-3 py-1 text-xs font-medium tracking-wider text-muted-foreground uppercase",
      className,
    )}
    {...props}
  />
);

const tableRowVariants = cva("", {
  defaultVariants: { variant: "default" },
  variants: { variant: { default: "", header: "bg-muted/40" } },
});

interface TableRowProps
  extends ComponentProps<typeof BaseTableRow>,
    VariantProps<typeof tableRowVariants> {}

export const TableRow: FC<TableRowProps> = ({
  className,
  variant,
  ...props
}) => (
  <BaseTableRow
    className={cn(tableRowVariants({ variant }), className)}
    {...props}
  />
);
